// Lightweight geometry helpers for placing markers on county polygons.
// Compact versions of the area-weighted centroid / area math used by MapView,
// good enough to drop one marker per owner on solid land.

type Ring = Array<[number, number]>;

const wrapLng = (lng: number): number => ((lng + 540) % 360) - 180;

function ringCentroid(ring: Ring): { lat: number; lng: number; area: number } | null {
    if (ring.length < 3) return null;
    const closed =
        ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
            ? ring
            : [...ring, ring[0]];

    // Unwrap longitudes so antimeridian-crossing rings don't average to garbage.
    const unwrapped: Ring = [];
    let prevLng = closed[0][0];
    let offset = 0;
    for (let i = 0; i < closed.length; i++) {
        const [lng, lat] = closed[i];
        if (i > 0) {
            const delta = lng - prevLng;
            if (delta > 180) offset -= 360;
            else if (delta < -180) offset += 360;
        }
        prevLng = lng;
        unwrapped.push([lng + offset, lat]);
    }

    let area = 0;
    let sumLng = 0;
    let sumLat = 0;
    for (let i = 0; i < unwrapped.length - 1; i++) {
        const [lng1, lat1] = unwrapped[i];
        const [lng2, lat2] = unwrapped[i + 1];
        const a = lng1 * lat2 - lng2 * lat1;
        area += a;
        sumLng += (lng1 + lng2) * a;
        sumLat += (lat1 + lat2) * a;
    }
    area /= 2;
    if (Math.abs(area) < 1e-10) {
        const avgLng = unwrapped.reduce((s, [lng]) => s + lng, 0) / (unwrapped.length - 1);
        const avgLat = unwrapped.reduce((s, [, lat]) => s + lat, 0) / (unwrapped.length - 1);
        return { lat: avgLat, lng: wrapLng(avgLng), area: 0 };
    }
    return { lat: sumLat / (6 * area), lng: wrapLng(sumLng / (6 * area)), area: Math.abs(area) };
}

export function featureCentroid(geometry: any): { lat: number; lng: number; area: number } | null {
    if (!geometry || !geometry.coordinates) return null;
    try {
        if (geometry.type === 'Polygon') {
            if (!geometry.coordinates[0]) return null;
            return ringCentroid(geometry.coordinates[0].map((c: any) => [c[0], c[1]] as [number, number]));
        }
        if (geometry.type === 'MultiPolygon') {
            const cents: Array<{ lat: number; lng: number; area: number }> = [];
            geometry.coordinates.forEach((poly: any) => {
                if (poly[0]) {
                    const c = ringCentroid(poly[0].map((p: any) => [p[0], p[1]] as [number, number]));
                    if (c && c.area > 0) cents.push(c);
                }
            });
            if (cents.length === 0) return null;
            const refLng = cents.reduce((a, b) => (b.area > a.area ? b : a)).lng;
            let wLat = 0;
            let wLng = 0;
            let total = 0;
            cents.forEach((c) => {
                let lng = c.lng;
                if (lng - refLng > 180) lng -= 360;
                else if (lng - refLng < -180) lng += 360;
                wLat += c.lat * c.area;
                wLng += lng * c.area;
                total += c.area;
            });
            if (total <= 0) return null;
            return { lat: wLat / total, lng: wrapLng(wLng / total), area: total };
        }
    } catch {
        return null;
    }
    return null;
}

export type EmpireSuperRegion = 'americas' | 'emea' | 'apac';

/** Coarse longitude bands — one pin per band mirrors county-map land blocks globally. */
export function empireSuperRegion(lng: number): EmpireSuperRegion {
    if (lng >= -30 && lng < 60) return 'emea';
    if (lng >= 60 || lng < -140) return 'apac';
    return 'americas';
}

export interface EmpirePinAnchor {
    lat: number;
    lng: number;
    countryName: string | null;
    region: EmpireSuperRegion;
    countriesInRegion: number;
}

export interface EmpirePinFeature {
    geometry: GeoJSON.Geometry;
    population: number;
    country_name?: string;
    country_iso?: string;
}

export interface EmpireCameraHomeHint {
    nationalityIso?: string | null;
    birthplaceLat?: number | null;
    birthplaceLng?: number | null;
}

function cameraTargetForHomeCountry(homeFeature: EmpirePinFeature): EmpireCameraTarget | null {
    const c = featureCentroid(homeFeature.geometry);
    if (!c) return null;

    const pinCenter: [number, number] = [c.lng, c.lat];
    const bounds = boundsFromFeatures([homeFeature]);
    if (!bounds) {
        return { bounds: null, center: pinCenter, pinCenter, zoom: 3.4 };
    }

    const [[west, south], [east, north]] = bounds;
    const lngSpan = east - west;
    const latSpan = north - south;
    const center: [number, number] = [(west + east) / 2, (south + north) / 2];

    if (lngSpan < 30 && latSpan < 22) {
        return {
            bounds: null,
            center,
            pinCenter,
            zoom: Math.min(4.8, 2.6 + Math.max(latSpan, lngSpan) * 0.14),
        };
    }

    return { bounds, center, pinCenter };
}

function findHomeOwnedFeature(
    ownedFeatures: EmpirePinFeature[],
    home?: EmpireCameraHomeHint,
): EmpirePinFeature | null {
    if (!home?.nationalityIso) return null;
    const iso = home.nationalityIso.trim().toUpperCase();
    if (!iso) return null;
    return ownedFeatures.find((f) => f.country_iso?.trim().toUpperCase() === iso) || null;
}

/** Place pins on owned countries only — one anchor per geographic super-region. */
export function resolveEmpirePinAnchors(features: EmpirePinFeature[]): EmpirePinAnchor[] {
    if (!features.length) return [];

    const byRegion = new Map<EmpireSuperRegion, Array<EmpirePinFeature & { lat: number; lng: number; area: number }>>();

    for (const f of features) {
        const c = featureCentroid(f.geometry);
        if (!c) continue;
        const region = empireSuperRegion(c.lng);
        const list = byRegion.get(region) || [];
        list.push({
            ...f,
            lat: c.lat,
            lng: c.lng,
            area: c.area,
        });
        byRegion.set(region, list);
    }

    const anchors: EmpirePinAnchor[] = [];
    for (const [region, list] of Array.from(byRegion.entries())) {
        let best = list[0];
        for (const item of list) {
            const bestPop = Number(best.population || 0);
            const itemPop = Number(item.population || 0);
            if (itemPop > bestPop || (itemPop === bestPop && item.area > best.area)) {
                best = item;
            }
        }
        anchors.push({
            lat: best.lat,
            lng: best.lng,
            countryName: best.country_name || null,
            region,
            countriesInRegion: list.length,
        });
    }

    return anchors.sort((a, b) => b.countriesInRegion - a.countriesInRegion);
}

export interface EmpireCameraTarget {
    bounds: [[number, number], [number, number]] | null;
    /** Population-weighted empire centroid (fallback). */
    center: [number, number];
    /** Primary standee pin — globe rotates here so the figure faces the camera upright. */
    pinCenter: [number, number];
    zoom?: number;
}

function forEachExteriorCoord(geometry: GeoJSON.Geometry, fn: (lng: number, lat: number) => void) {
    if (geometry.type === 'Polygon') {
        (geometry.coordinates[0] || []).forEach((c) => fn(c[0], c[1]));
        return;
    }
    if (geometry.type === 'MultiPolygon') {
        geometry.coordinates.forEach((poly) => (poly[0] || []).forEach((c) => fn(c[0], c[1])));
    }
}

function boundsFromFeatures(features: EmpirePinFeature[]): [[number, number], [number, number]] | null {
    let minLng = Infinity;
    let maxLng = -Infinity;
    let minLat = 90;
    let maxLat = -90;
    let any = false;

    for (const f of features) {
        forEachExteriorCoord(f.geometry, (lng, lat) => {
            any = true;
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
        });
    }

    if (!any) return null;
    return [[minLng, minLat], [maxLng, maxLat]];
}

function populationByRegion(features: EmpirePinFeature[]): Map<EmpireSuperRegion, number> {
    const totals = new Map<EmpireSuperRegion, number>();
    for (const f of features) {
        const c = featureCentroid(f.geometry);
        if (!c) continue;
        const region = empireSuperRegion(c.lng);
        totals.set(region, (totals.get(region) || 0) + Number(f.population || 1));
    }
    return totals;
}

/** Camera target for map.flyTo / fitBounds when focusing a fighter empire. */
export function computeEmpireCameraTarget(
    ownedFeatures: EmpirePinFeature[],
    home?: EmpireCameraHomeHint,
): EmpireCameraTarget | null {
    if (!ownedFeatures.length) return null;

    const homeFeature = findHomeOwnedFeature(ownedFeatures, home);
    if (homeFeature) {
        const homeTarget = cameraTargetForHomeCountry(homeFeature);
        if (homeTarget) return homeTarget;
    }

    if (home?.birthplaceLat != null && home?.birthplaceLng != null) {
        const pinCenter: [number, number] = [home.birthplaceLng, home.birthplaceLat];
        return {
            bounds: null,
            center: pinCenter,
            pinCenter,
            zoom: 3.4,
        };
    }

    let subset = ownedFeatures;
    const globalBounds = boundsFromFeatures(ownedFeatures);
    if (!globalBounds) return null;

    const [[minLng, minLat], [maxLng, maxLat]] = globalBounds;
    let lngSpan = maxLng - minLng;
    let latSpan = maxLat - minLat;

    // Scattered global empires — frame the largest regional block (Americas / EMEA / APAC).
    if (lngSpan > 100 || latSpan > 75) {
        const byRegion = populationByRegion(ownedFeatures);
        let bestRegion: EmpireSuperRegion = 'americas';
        let bestPop = -1;
        byRegion.forEach((pop, region) => {
            if (pop > bestPop) {
                bestPop = pop;
                bestRegion = region;
            }
        });
        subset = ownedFeatures.filter((f) => {
            const c = featureCentroid(f.geometry);
            return c && empireSuperRegion(c.lng) === bestRegion;
        });
        if (!subset.length) subset = ownedFeatures;
    }

    const bounds = boundsFromFeatures(subset);
    if (!bounds) return null;

    const [[west, south], [east, north]] = bounds;
    lngSpan = east - west;
    latSpan = north - south;

    let wLat = 0;
    let wLng = 0;
    let popSum = 0;
    for (const f of subset) {
        const c = featureCentroid(f.geometry);
        if (!c) continue;
        const pop = Number(f.population || 1);
        wLat += c.lat * pop;
        wLng += c.lng * pop;
        popSum += pop;
    }
    const center: [number, number] = popSum > 0
        ? [wLng / popSum, wLat / popSum]
        : [(west + east) / 2, (south + north) / 2];

    const anchors = resolveEmpirePinAnchors(subset);
    const primaryPin = anchors[0];
    const pinCenter: [number, number] = primaryPin
        ? [primaryPin.lng, primaryPin.lat]
        : center;

    if (subset.length === 1 && lngSpan < 25 && latSpan < 20) {
        return { bounds: null, center, pinCenter, zoom: Math.min(4.2, 2.2 + latSpan * 0.08) };
    }

    if (lngSpan < 95 && latSpan < 65) {
        return { bounds, center, pinCenter };
    }

    return { bounds: null, center, pinCenter, zoom: lngSpan > 60 ? 1.6 : 2.2 };
}
